import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Space,
  Typography,
  Table,
  Tag,
  Statistic,
  Row,
  Col,
  Alert,
  Button,
  Select,
  Layout,
  message,
} from "antd";
import {
  CarOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosConfig";
import * as XLSX from "xlsx";
import logoAyasa from "../assets/ayasa.png";
import logoRenault from "../assets/0emwie0m.png";
import logoNissan from "../assets/nissan.svg";
import logoFoton from "../assets/foton.svg";
import logoBaic from "../assets/baic.svg";

const { Title, Text } = Typography;
const { Option } = Select;
const { Content } = Layout;

// Paleta de colores moderna y clara
const colors = {
  primary: "#4096ff",
  secondary: "#52c41a",
  accent: "#722ed1",
  background: "#f7f9fc",
  cardBg: "#ffffff",
  lightBorder: "#eaedf3",
  text: "#2e3a59",
  textSecondary: "#8f95a5",
  shadow: "rgba(17, 20, 45, 0.08)",
  diurno: "#5b8ff9",
  nocturno: "#8b60ed",
  success: "#52c41a",
};

const logoContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "15px",
};

const logoStyle = {
  height: "32px",
  objectFit: "contain",
};

const renaultLogoStyle = {
  height: "40px",
  objectFit: "contain",
};

const titleContainerStyle = {
  display: "flex",
  alignItems: "center",
  flex: 1,
  justifyContent: "center",
};

const cardStyle = {
  borderRadius: "12px",
  boxShadow: `0 4px 20px ${colors.shadow}`,
  border: "none",
  overflow: "hidden",
  background: colors.cardBg,
  width: "100%",
};

const statisticCardStyle = {
  ...cardStyle,
  height: "100%",
  transition: "all 0.3s ease",
  cursor: "pointer",
  padding: "16px",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: `0 8px 30px ${colors.shadow}`,
  },
};

// Estilos para el menú
const menuStyle = {
  backgroundColor: colors.cardBg,
  borderRadius: "8px",
  boxShadow: `0 4px 12px ${colors.shadow}`,
};

const menuItemStyle = {
  "&:hover": {
    backgroundColor: colors.background,
  },
};

// Definir pageSize como una constante fuera del componente
const DEFAULT_PAGE_SIZE = 100;

const SalesLocationDistances = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [cantones, setCantones] = useState([]);
  const [cantonesDisponibles, setCantonesDisponibles] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [puntosVentaDisponibles, setPuntosVentaDisponibles] = useState([]);
  const [años, setAños] = useState([]);
  const [filtros, setFiltros] = useState({
    provincia: "TODOS",
    canton: "TODOS",
    puntoVenta: "TODOS",
    año: "TODOS",
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [stats, setStats] = useState({
    totalVehicles: 0,
    avgDayDistance: 0,
    avgNightDistance: 0,
    maxDistance: 0,
  });
  const [error, setError] = useState(null);
  const [cantonProvinciaMap, setCantonProvinciaMap] = useState({});

  // Detectar si estamos en un dispositivo móvil
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Efecto para detectar cambios en el tamaño de la pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Función para verificar si un registro contiene "OTROS" en campos relevantes
  const contieneOtros = useCallback((item) => {
    if (!item) return false;

    // Lista completa de campos a revisar
    const camposARevisar = [
      item.day_province,
      item.day_canton,
      item.night_province,
      item.night_canton,
      item.SalesLocationDistance?.agencia_name,
      item.day_sector,
      item.night_sector,
      item.day_city,
      item.night_city,
      item.day_address,
      item.night_address,
    ];

    // Palabras clave a buscar (en mayúsculas para comparación insensible a mayúsculas/minúsculas)
    const palabrasClave = [
      "OTRO",
      "OTROS",
      "OTHER",
      "DESCONOCIDO",
      "N/A",
      "SIN DEFINIR",
    ];

    // Verificar si algún campo contiene alguna de las palabras clave
    return camposARevisar.some((campo) => {
      if (!campo) return false;
      const campoUpper = campo.toUpperCase();
      return palabrasClave.some((palabra) => campoUpper.includes(palabra));
    });
  }, []);

  // Función para ordenar los datos con "OTROS" al final
  const ordenarConOtrosAlFinal = useCallback(
    (datos) => {
      if (!datos || !Array.isArray(datos)) return [];

      console.log("Ordenando datos, total:", datos.length);

      // Contar cuántos registros contienen "OTROS" antes de ordenar
      const conteoOtrosAntes = datos.filter((item) =>
        contieneOtros(item)
      ).length;
      console.log("Registros con OTROS antes de ordenar:", conteoOtrosAntes);

      // Crear una copia para no modificar el original
      const datosOrdenados = [...datos].sort((a, b) => {
        const aContieneOtros = contieneOtros(a);
        const bContieneOtros = contieneOtros(b);

        if (aContieneOtros && !bContieneOtros) {
          return 1; // a va después de b
        }
        if (!aContieneOtros && bContieneOtros) {
          return -1; // a va antes que b
        }
        return 0; // mantener el orden original
      });

      // Contar cuántos registros contienen "OTROS" después de ordenar
      const conteoOtrosDespues = datosOrdenados.filter((item) =>
        contieneOtros(item)
      ).length;
      console.log(
        "Registros con OTROS después de ordenar:",
        conteoOtrosDespues
      );

      // Verificar que los registros con "OTROS" estén al final
      const ultimosRegistros = datosOrdenados.slice(-conteoOtrosDespues);
      const todosUltimosContienenOtros = ultimosRegistros.every((item) =>
        contieneOtros(item)
      );
      console.log(
        "¿Todos los últimos registros contienen OTROS?",
        todosUltimosContienenOtros
      );

      return datosOrdenados;
    },
    [contieneOtros]
  );

  // Estado para almacenar todos los datos sin paginar
  const [allData, setAllData] = useState([]);

  // Función para cargar todos los datos y aplicar ordenamiento y paginación en el cliente
  const fetchData = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE, search = "") => {
      setLoading(true);
      setError(null);
      try {
        // Preparar parámetros de filtro
        const params = {
          // Especificamos un límite alto para obtener todos los datos
          limit: 50000,
          search,
        };

        // Agregar filtros solo si no son "TODOS" o están definidos
        if (filtros.provincia !== "TODOS") {
          params.provincia = filtros.provincia;
        }
        if (filtros.canton !== "TODOS") {
          params.canton = filtros.canton;
        }
        if (filtros.puntoVenta !== "TODOS") {
          params.puntoVenta = filtros.puntoVenta;
        }
        if (filtros.año !== "TODOS" && filtros.año !== undefined) {
          params.año = filtros.año;
        }

        console.log("Parámetros de filtrado enviados:", params);

        const [responseData, responseStats] = await Promise.all([
          axiosInstance.get("/sales-location-verification/matches", {
            params: params,
          }),
          axiosInstance.get("/sales-location-verification/global-stats", {
            params: params,
          }),
        ]);

        console.log("Datos recibidos:", responseData.data);
        console.log("Estadísticas recibidas:", responseStats.data);

        // Procesar todos los datos
        const distances = responseData.data.data.map((item) => ({
          ...item,
          key: item.chasis,
          day_distance:
            parseFloat(item.SalesLocationDistance?.day_distance_km) || 0,
          night_distance:
            parseFloat(item.SalesLocationDistance?.night_distance_km) || 0,
        }));

        // Ordenar TODOS los datos para que los que contienen "OTROS" aparezcan al final
        const sortedDistances = ordenarConOtrosAlFinal(distances);

        // Guardar todos los datos ordenados
        setAllData(sortedDistances);

        // Calcular el total de registros
        const total = sortedDistances.length;

        console.log(`Total de registros: ${total}`);

        // Usar el tamaño de página actual o el proporcionado
        const currentPageSize = pagination.pageSize || pageSize;

        // Calcular índices para la paginación actual
        const startIndex = (page - 1) * currentPageSize;
        const endIndex = Math.min(startIndex + currentPageSize, total);

        console.log(
          `Mostrando registros ${startIndex + 1}-${endIndex} de ${total}`
        );

        // Obtener solo los datos para la página actual
        const paginatedData = sortedDistances.slice(startIndex, endIndex);

        // Actualizar el estado con los datos paginados
        setData(paginatedData);
        setPagination({
          current: page,
          pageSize: currentPageSize,
          total: total,
        });
        setStats(responseStats.data);

        // Mostrar un mensaje sobre los filtros aplicados
        if (filtrosAplicados) {
          message.success(
            `Se encontraron ${total} registros con los filtros aplicados`
          );
          setFiltrosAplicados(false);
        }
      } catch (err) {
        if (err.response?.status === 429) {
          setError(
            "Demasiadas peticiones. Por favor, espera unos minutos antes de intentar de nuevo."
          );
        } else {
          setError(
            "Error al cargar los datos: " +
              (err.response?.data?.error || err.message)
          );
        }
        console.error("Error al cargar datos:", err);
      } finally {
        setLoading(false);
      }
    },
    [filtros, filtrosAplicados, ordenarConOtrosAlFinal, pagination.pageSize]
  );

  // Cargar datos iniciales para los filtros
  const cargarDatosIniciales = async () => {
    try {
      // Cargar provincias únicas
      const responseProvincias = await axiosInstance.get(
        "/sales-location-verification/provincias"
      );
      // Filtrar la opción "OTRO" de la lista de provincias
      const provinciasFiltradas = responseProvincias.data.filter(
        (provincia) => provincia !== "OTRO"
      );
      setProvincias(["TODOS", ...provinciasFiltradas]);

      // Cargar cantones únicos
      const responseCantones = await axiosInstance.get(
        "/sales-location-verification/cantones"
      );
      const cantonesList = ["TODOS", ...responseCantones.data];
      setCantones(cantonesList);
      setCantonesDisponibles(cantonesList);

      // Cargar puntos de venta
      const responsePuntosVenta = await axiosInstance.get("/agencies");
      const puntosVentaList = [
        { codigo: "TODOS", name: "TODOS" },
        ...responsePuntosVenta.data,
      ];
      setPuntosVenta(puntosVentaList);
      setPuntosVentaDisponibles(puntosVentaList);

      // Generar años desde 2020 hasta el año actual
      const añoActual = new Date().getFullYear();
      const listaAños = [
        "TODOS",
        ...Array.from({ length: añoActual - 2019 }, (_, i) => añoActual - i),
      ];
      setAños(listaAños);

      // Mejorado: Obtener TODOS los datos para crear un mapeo completo
      try {
        // Primero intentamos obtener todos los registros sin límite
        const allMatches = await axiosInstance.get(
          "/sales-location-verification/matches",
          { params: { limit: 10000 } } // Aumentamos el límite significativamente
        );

        // Crear mapeo mejorado de cantón -> provincia
        const cantonToProvince = {};

        // Procesamos los registros diurnos
        allMatches.data.data.forEach((item) => {
          if (item.day_canton && item.day_province) {
            cantonToProvince[item.day_canton] = item.day_province;
          }
        });

        // Procesamos los registros nocturnos
        allMatches.data.data.forEach((item) => {
          if (item.night_canton && item.night_province) {
            cantonToProvince[item.night_canton] = item.night_province;
          }
        });

        console.log("Mapeo de cantones a provincias:", cantonToProvince);
        console.log(
          "Total de cantones mapeados:",
          Object.keys(cantonToProvince).length
        );

        setCantonProvinciaMap(cantonToProvince);
      } catch (err) {
        console.error("Error al crear mapeo completo:", err);
        // Si falla, intentamos un enfoque alternativo directo

        // Mapeo manual para provincias del Ecuador
        const manualMapping = {
          AZUAY: ["CUENCA", "GUALACEO", "PAUTE"],
          GUAYAS: ["GUAYAQUIL", "DAULE", "SAMBORONDÓN", "DURÁN"],
          PICHINCHA: ["QUITO", "RUMIÑAHUI", "MEJÍA"],
          MANABI: ["PORTOVIEJO", "MANTA", "MONTECRISTI"],
          "EL ORO": ["MACHALA", "SANTA ROSA", "PASAJE"],
          TUNGURAHUA: ["AMBATO", "BAÑOS", "PELILEO"],
          CHIMBORAZO: ["RIOBAMBA", "GUANO", "COLTA"],
          LOJA: ["LOJA", "CATAMAYO", "SARAGURO"],
          IMBABURA: ["IBARRA", "OTAVALO", "COTACACHI"],
          ESMERALDAS: ["ESMERALDAS", "ATACAMES", "MUISNE"],
          // Más provincias según sea necesario
        };

        // Invertir el mapeo para obtener cantón -> provincia
        const cantonProvinceMap = {};
        Object.entries(manualMapping).forEach(([provincia, cantones]) => {
          cantones.forEach((canton) => {
            cantonProvinceMap[canton] = provincia;
          });
        });

        console.log("Usando mapeo alternativo:", cantonProvinceMap);
        setCantonProvinciaMap(cantonProvinceMap);
      }

      // Cargar datos iniciales después de configurar los filtros
      fetchData(1, DEFAULT_PAGE_SIZE, "");
    } catch (err) {
      setError("Error al cargar datos de filtros: " + err.message);
      console.error("Error al cargar datos iniciales:", err);
    }
  };

  useEffect(() => {
    cargarDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Actualizar cantones disponibles cuando cambia la provincia
  useEffect(() => {
    if (filtros.provincia === "TODOS") {
      setCantonesDisponibles(cantones);
    } else {
      // Mejorado: Buscar todos los cantones para la provincia seleccionada
      const cantonesEnProvincia = ["TODOS"];

      // Agregar todos los cantones que pertenecen a la provincia seleccionada
      Object.entries(cantonProvinciaMap).forEach(([canton, provincia]) => {
        if (
          provincia === filtros.provincia &&
          !cantonesEnProvincia.includes(canton)
        ) {
          cantonesEnProvincia.push(canton);
        }
      });

      console.log(
        `Cantones en provincia ${filtros.provincia}:`,
        cantonesEnProvincia
      );

      setCantonesDisponibles(cantonesEnProvincia);

      // Si el cantón seleccionado no está en la nueva lista, resetear a "TODOS"
      if (!cantonesEnProvincia.includes(filtros.canton)) {
        setFiltros((prev) => ({
          ...prev,
          canton: "TODOS",
        }));
      }
    }
  }, [filtros.provincia, cantones, cantonProvinciaMap, filtros.canton]);

  // Actualizar puntos de venta disponibles cuando cambia la provincia solamente
  useEffect(() => {
    const cargarPuntosVenta = async () => {
      try {
        // Construir parámetros para la solicitud
        const params = {};
        if (filtros.provincia !== "TODOS") {
          params.provincia = filtros.provincia;
        }

        console.log(
          "Cargando puntos de venta con filtro de provincia:",
          filtros.provincia
        );

        // Si no hay filtro de provincia, cargar todos los puntos de venta
        if (Object.keys(params).length === 0) {
          setPuntosVentaDisponibles(puntosVenta);
          return;
        }

        // Cargar los puntos de venta filtrados solo por provincia
        const response = await axiosInstance.get(
          "/sales-location-verification/puntos-venta",
          { params }
        );

        console.log("Puntos de venta recibidos:", response.data);
        setPuntosVentaDisponibles(response.data);

        // Si el punto de venta seleccionado no está disponible, resetear a "TODOS"
        const codigosDisponibles = response.data.map((pv) => pv.codigo);
        if (!codigosDisponibles.includes(filtros.puntoVenta)) {
          setFiltros((prev) => ({ ...prev, puntoVenta: "TODOS" }));
        }
      } catch (error) {
        console.error("Error al cargar puntos de venta filtrados:", error);
        // Mantener la lista existente en caso de error
      }
    };

    // Cargar los puntos de venta disponibles
    cargarPuntosVenta();
  }, [filtros.provincia, puntosVenta, filtros.puntoVenta]);

  const handleFiltroChange = (valor, tipo) => {
    setFiltros((prev) => {
      const newFiltros = { ...prev, [tipo]: valor };

      // Si se cambia la provincia, resetear cantón
      if (tipo === "provincia" && valor !== prev.provincia) {
        newFiltros.canton = "TODOS";
      }

      return newFiltros;
    });
  };

  const aplicarFiltros = () => {
    console.log("Aplicando filtros:", filtros);
    setFiltrosAplicados(true);
    // Resetear a la primera página y aplicar filtros
    fetchData(1, DEFAULT_PAGE_SIZE, searchText);
  };

  const limpiarFiltros = () => {
    setFiltros({
      provincia: "TODOS",
      canton: "TODOS",
      puntoVenta: "TODOS",
      año: "TODOS",
    });
    setSearchText("");
    setFiltrosAplicados(true);
    // Cargar datos con los filtros limpiados
    fetchData(1, DEFAULT_PAGE_SIZE, "");
    message.info("Filtros limpiados");
  };

  const handleTableChange = (newPagination) => {
    // Verificar si cambió la página o el tamaño de página
    if (
      newPagination.current !== pagination.current ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      // Calcular índices para la nueva página con el nuevo tamaño de página
      const startIndex = (newPagination.current - 1) * newPagination.pageSize;
      const endIndex = Math.min(
        startIndex + newPagination.pageSize,
        allData.length
      );

      // Obtener los datos para la nueva página
      const paginatedData = allData.slice(startIndex, endIndex);

      // Actualizar el estado con los datos paginados
      setData(paginatedData);
      setPagination({
        ...pagination,
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      });

      console.log(
        `Mostrando registros ${startIndex + 1}-${endIndex} de ${allData.length}`
      );
    }
  };

  const exportToExcel = async () => {
    try {
      setLoading(true);

      // Usar los datos ya cargados en el estado
      if (allData.length === 0) {
        message.warning("No hay datos para exportar con los filtros actuales");
        setLoading(false);
        return;
      }

      // Usar las estadísticas ya cargadas en el estado
      const statsData = stats;

      // Los datos ya están ordenados con "OTROS" al final
      const exportData = allData.map((item) => ({
        Chasis: item.chasis,
        Agencia: item.SalesLocationDistance?.agencia_name || "",
        "Provincia (Diurno)": item.day_province || "",
        "Cantón (Diurno)": item.day_canton || "",
        "Provincia (Nocturno)": item.night_province || "",
        "Cantón (Nocturno)": item.night_canton || "",
        "Distancia Diurna (km)":
          item.SalesLocationDistance?.day_distance_km || "",
        "Distancia Nocturna (km)":
          item.SalesLocationDistance?.night_distance_km || "",
        "Fecha Venta": new Date(item.fecha_venta).toLocaleDateString(),
      }));

      // Agregar filas en blanco y estadísticas
      exportData.push(
        {}, // Fila en blanco
        {
          Chasis: "ESTADÍSTICAS GLOBALES",
          Agencia: "",
          "Provincia (Diurno)": "",
          "Cantón (Diurno)": "",
          "Provincia (Nocturno)": "",
          "Cantón (Nocturno)": "",
          "Distancia Diurna (km)": "",
          "Distancia Nocturna (km)": "",
          "Fecha Venta": "",
        },
        {
          Chasis: "Total de Vehículos",
          Agencia: allData.length,
        },
        {
          Chasis: "Distancia Promedio Diurna",
          Agencia: `${statsData.avgDayDistance.toFixed(2)} km`,
        },
        {
          Chasis: "Distancia Promedio Nocturna",
          Agencia: `${statsData.avgNightDistance.toFixed(2)} km`,
        }
      );

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Distancias");

      // Agregar información de filtros aplicados
      const filtrosInfo = [
        {},
        { Chasis: "FILTROS APLICADOS" },
        { Chasis: "Provincia", Agencia: filtros.provincia },
        { Chasis: "Cantón", Agencia: filtros.canton },
        {
          Chasis: "Agencia de Venta",
          Agencia:
            filtros.puntoVenta !== "TODOS"
              ? puntosVenta.find((p) => p.codigo === filtros.puntoVenta)
                  ?.name || filtros.puntoVenta
              : "TODOS",
        },
        { Chasis: "Año", Agencia: filtros.año || "TODOS" },
        { Chasis: "Búsqueda", Agencia: searchText || "N/A" },
      ];

      // Agregar filtros al final
      filtrosInfo.forEach((item) => {
        exportData.push(item);
      });

      // Ajustar el ancho de las columnas
      const columnsWidth = [
        { wch: 20 }, // Chasis
        { wch: 30 }, // Agencia
        { wch: 25 }, // Provincia (Diurno)
        { wch: 25 }, // Cantón (Diurno)
        { wch: 25 }, // Provincia (Nocturno)
        { wch: 25 }, // Cantón (Nocturno)
        { wch: 20 }, // Distancia Diurna
        { wch: 20 }, // Distancia Nocturna
        { wch: 15 }, // Fecha Venta
      ];
      ws["!cols"] = columnsWidth;

      XLSX.writeFile(
        wb,
        `distancias_vehiculos_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      message.success(`Se exportaron ${allData.length} registros a Excel`);
    } catch (error) {
      setError("Error al exportar datos: " + error.message);
      console.error("Error al exportar a Excel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Estilos para los selectores
  const selectStyle = {
    width: "100%",
    borderRadius: "8px",
    boxShadow: `0 1px 2px ${colors.shadow}`,
  };

  // Estilos para los botones primarios
  const primaryButtonStyle = {
    borderRadius: "8px",
    fontWeight: "500",
    boxShadow: `0 2px 4px ${colors.shadow}`,
  };

  // Estilos para los botones secundarios
  const secondaryButtonStyle = {
    borderRadius: "8px",
    fontWeight: "500",
    border: `1px solid ${colors.lightBorder}`,
  };

  const columns = [
    {
      title: "Chasis",
      dataIndex: "chasis",
      key: "chasis",
      width: 150,
    },
    {
      title: "Punto de Venta",
      dataIndex: ["SalesLocationDistance", "agencia_name"],
      key: "agencia",
      width: 200,
      render: (text) => text || "N/A",
    },
    {
      title: "Distancia Entre Punto de Venta y Lugar de Trabajo (Diurno)",
      dataIndex: "day_canton",
      key: "day_location",
      align: "center",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "280px", // Ancho fijo para mantener alineación consistente
            }}
          >
            <div
              style={{
                width: "30px",
                display: "flex",
                justifyContent: "center",
                flexShrink: 0, // Evita que el icono se comprima
              }}
            >
              <EnvironmentOutlined style={{ color: colors.diurno }} />
            </div>
            <div
              style={{
                minWidth: "150px",
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginLeft: "8px",
                flexGrow: 1, // Permite que el texto ocupe el espacio disponible
              }}
            >
              <Text style={{ color: colors.text }}>
                {record.day_province ? `${record.day_province} - ` : ""}
                {text || "N/A"}
              </Text>
            </div>
            <Tag
              color={colors.diurno}
              style={{
                borderRadius: "20px",
                padding: "0 10px",
                marginLeft: "8px",
                flexShrink: 0, // Evita que el tag se comprima
              }}
            >
              {typeof record.SalesLocationDistance?.day_distance_km === "number"
                ? `${record.SalesLocationDistance.day_distance_km.toFixed(
                    2
                  )} km`
                : "N/A"}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Distancia Entre Punto de Venta y Domicilio (Nocturno)",
      dataIndex: "night_canton",
      key: "night_location",
      align: "center",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "280px", // Ancho fijo para mantener alineación consistente
            }}
          >
            <div
              style={{
                width: "30px",
                display: "flex",
                justifyContent: "center",
                flexShrink: 0, // Evita que el icono se comprima
              }}
            >
              <EnvironmentOutlined style={{ color: colors.nocturno }} />
            </div>
            <div
              style={{
                minWidth: "150px",
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginLeft: "8px",
                flexGrow: 1, // Permite que el texto ocupe el espacio disponible
              }}
            >
              <Text style={{ color: colors.text }}>
                {record.night_province ? `${record.night_province} - ` : ""}
                {text || "N/A"}
              </Text>
            </div>
            <Tag
              color={colors.nocturno}
              style={{
                borderRadius: "20px",
                padding: "0 10px",
                marginLeft: "8px",
                flexShrink: 0, // Evita que el tag se comprima
              }}
            >
              {typeof record.SalesLocationDistance?.night_distance_km ===
              "number"
                ? `${record.SalesLocationDistance.night_distance_km.toFixed(
                    2
                  )} km`
                : "N/A"}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Fecha Venta",
      dataIndex: "fecha_venta",
      key: "fecha_venta",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "N/A"),
      width: 120,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: colors.background }}>
      <Content style={{ padding: isMobile ? "12px" : "24px" }}>
        <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: isMobile ? "8px 16px" : "10px 24px",
              height: isMobile ? "56px" : "64px",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <div style={logoContainerStyle}>
              <img src={logoAyasa} alt="AYASA Logo" style={logoStyle} />
              <img
                src={logoRenault}
                alt="Renault Logo"
                style={renaultLogoStyle}
              />
              <img
                src={logoNissan}
                alt="Nissan Logo"
                style={renaultLogoStyle}
              />
              <img
                src={logoFoton}
                alt="Foton Logo"
                style={renaultLogoStyle}
              />
              <img
                src={logoBaic}
                alt="BAIC Logo"
                style={renaultLogoStyle}
              />
            </div>
            <div style={titleContainerStyle}>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: colors.text,
                  fontSize: isMobile ? "14px" : "18px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {isMobile
                  ? "Distancias Punto de Venta"
                  : "Distancias Entre Punto de Venta y Domicilios / Lugares de Trabajo"}
              </Title>
            </div>
          </div>
        </Card>

        <div style={{ marginTop: "24px" }}>
          <Space
            direction="vertical"
            size={isMobile ? "middle" : "large"}
            style={{ width: "100%" }}
          >
            <Card
              style={cardStyle}
              bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
            >
              <Title
                level={4}
                style={{
                  marginBottom: isMobile ? "16px" : "24px",
                  color: colors.text,
                  fontSize: isMobile ? "16px" : "inherit",
                }}
              >
                <FilterOutlined style={{ color: colors.primary }} /> Filtros
              </Title>
              <Row gutter={[isMobile ? 12 : 24, isMobile ? 12 : 24]}>
                {/* Reordenados los filtros: Provincia primero, luego Cantón, luego Punto de Venta */}
                <Col xs={24} sm={12} md={6}>
                  <Text
                    style={{
                      display: "block",
                      marginBottom: isMobile ? "4px" : "8px",
                      color: colors.text,
                      fontWeight: "500",
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    Provincia
                  </Text>
                  <Select
                    style={selectStyle}
                    placeholder="Seleccione Provincia"
                    value={filtros.provincia}
                    onChange={(valor) => handleFiltroChange(valor, "provincia")}
                    allowClear={false}
                    dropdownStyle={menuStyle}
                    size={isMobile ? "small" : "middle"}
                  >
                    {provincias.map((provincia) => (
                      <Option
                        key={provincia}
                        value={provincia}
                        style={menuItemStyle}
                      >
                        {provincia}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text
                    style={{
                      display: "block",
                      marginBottom: isMobile ? "4px" : "8px",
                      color: colors.text,
                      fontWeight: "500",
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    Cantón
                  </Text>
                  <Select
                    style={selectStyle}
                    placeholder="Seleccione Cantón"
                    value={filtros.canton}
                    onChange={(valor) => handleFiltroChange(valor, "canton")}
                    allowClear={false}
                    dropdownStyle={menuStyle}
                    disabled={cantonesDisponibles.length <= 1}
                    size={isMobile ? "small" : "middle"}
                  >
                    {cantonesDisponibles.map((canton) => (
                      <Option key={canton} value={canton} style={menuItemStyle}>
                        {canton}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text
                    style={{
                      display: "block",
                      marginBottom: isMobile ? "4px" : "8px",
                      color: colors.text,
                      fontWeight: "500",
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    Punto de Venta
                  </Text>
                  <Select
                    style={selectStyle}
                    placeholder="Seleccione Punto de Venta"
                    value={filtros.puntoVenta}
                    onChange={(valor) =>
                      handleFiltroChange(valor, "puntoVenta")
                    }
                    allowClear={false}
                    dropdownStyle={menuStyle}
                    size={isMobile ? "small" : "middle"}
                  >
                    {puntosVentaDisponibles.map((punto) => (
                      <Option
                        key={punto.codigo}
                        value={punto.codigo}
                        style={menuItemStyle}
                      >
                        {punto.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Text
                    style={{
                      display: "block",
                      marginBottom: isMobile ? "4px" : "8px",
                      color: colors.text,
                      fontWeight: "500",
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    Año de Venta
                  </Text>
                  <Select
                    style={selectStyle}
                    placeholder="Seleccione Año"
                    value={filtros.año}
                    onChange={(valor) => handleFiltroChange(valor, "año")}
                    allowClear={false}
                    dropdownStyle={menuStyle}
                    size={isMobile ? "small" : "middle"}
                  >
                    {años.map((año) => (
                      <Option key={año} value={año} style={menuItemStyle}>
                        {año}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24}>
                  <Space
                    size="middle"
                    style={{
                      display: "flex",
                      justifyContent: isMobile ? "center" : "flex-start",
                    }}
                  >
                    <Button
                      type="primary"
                      icon={<FilterOutlined />}
                      onClick={aplicarFiltros}
                      loading={loading}
                      size={isMobile ? "small" : "middle"}
                      style={{
                        ...primaryButtonStyle,
                        height: isMobile ? "28px" : "32px",
                        padding: "0 15px",
                        fontSize: isMobile ? "12px" : "14px",
                      }}
                    >
                      Aplicar Filtros
                    </Button>
                    <Button
                      onClick={limpiarFiltros}
                      disabled={loading}
                      size={isMobile ? "small" : "middle"}
                      style={{
                        ...secondaryButtonStyle,
                        height: isMobile ? "28px" : "32px",
                        padding: "0 15px",
                        fontSize: isMobile ? "12px" : "14px",
                      }}
                    >
                      Limpiar Filtros
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Row
              gutter={[isMobile ? 12 : 24, isMobile ? 12 : 24]}
              justify="center"
              align="middle"
            >
              <Col xs={24} sm={12} lg={8}>
                <Card style={statisticCardStyle} hoverable>
                  <Statistic
                    title={
                      <span
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          color: colors.secondary,
                          textAlign: "center",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        {isMobile
                          ? "Total Base de Ventas"
                          : "Total Base de Ventas vs Vehículos con Dispositivo"}
                      </span>
                    }
                    value={stats.totalVehicles}
                    prefix={<CarOutlined style={{ color: colors.secondary }} />}
                    valueStyle={{
                      color: colors.secondary,
                      fontSize: isMobile ? 24 : 28,
                      textAlign: "center",
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card style={statisticCardStyle} hoverable>
                  <Statistic
                    title={
                      <span
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          color: colors.diurno,
                          textAlign: "center",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        {isMobile
                          ? "Dist. Promedio Trabajo (Diurno)"
                          : "Distancia Promedio Entre Punto de Venta y Lugar de Trabajo (Diurno)"}
                      </span>
                    }
                    value={stats.avgDayDistance}
                    precision={2}
                    suffix="km"
                    prefix={
                      <EnvironmentOutlined style={{ color: colors.diurno }} />
                    }
                    valueStyle={{
                      color: colors.diurno,
                      fontSize: isMobile ? 24 : 28,
                      textAlign: "center",
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card style={statisticCardStyle} hoverable>
                  <Statistic
                    title={
                      <span
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          color: colors.nocturno,
                          textAlign: "center",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        {isMobile
                          ? "Dist. Promedio Domicilio (Nocturno)"
                          : "Distancia Promedio Entre Punto de Venta y Domicilio (Nocturno)"}
                      </span>
                    }
                    value={stats.avgNightDistance}
                    precision={2}
                    suffix="km"
                    prefix={
                      <EnvironmentOutlined style={{ color: colors.nocturno }} />
                    }
                    valueStyle={{
                      color: colors.nocturno,
                      fontSize: isMobile ? 24 : 28,
                      textAlign: "center",
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            <Card
              style={cardStyle}
              bodyStyle={{ padding: isMobile ? "16px" : "24px" }}
            >
              <Space
                style={{
                  marginBottom: isMobile ? 12 : 16,
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    color: colors.text,
                    fontSize: isMobile ? "16px" : "inherit",
                    marginBottom: isMobile ? "8px" : 0,
                    width: isMobile ? "100%" : "auto",
                    textAlign: isMobile ? "center" : "left",
                  }}
                >
                  <CarOutlined style={{ color: colors.primary }} /> Registro de
                  Distancias
                </Title>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    width: isMobile ? "100%" : "auto",
                    justifyContent: isMobile ? "center" : "flex-end",
                  }}
                >
                  <Text
                    style={{
                      color: colors.textSecondary,
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    {pagination.total}{" "}
                    {pagination.total === 1
                      ? "registro encontrado"
                      : "registros encontrados"}
                  </Text>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={exportToExcel}
                    loading={loading}
                    size={isMobile ? "small" : "middle"}
                    style={{
                      backgroundColor: colors.success,
                      borderColor: colors.success,
                      fontWeight: "500",
                      borderRadius: "8px",
                      height: isMobile ? "28px" : "32px",
                      padding: "0 15px",
                      fontSize: isMobile ? "12px" : "14px",
                    }}
                  >
                    Exportar
                  </Button>
                </div>
              </Space>

              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16, borderRadius: "8px" }}
                />
              )}

              <Table
                columns={columns}
                dataSource={data}
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} de ${total} registros`,
                }}
                onChange={handleTableChange}
                loading={loading}
                rowKey="chasis"
                scroll={{ x: 1000 }}
                style={{ marginTop: isMobile ? 12 : 16 }}
                locale={{ emptyText: "No hay datos disponibles" }}
                rowClassName={(record) =>
                  contieneOtros(record)
                    ? "ant-table-row-custom otros-row"
                    : "ant-table-row-custom"
                }
                size={isMobile ? "small" : "middle"}
                className="modern-table"
              />
            </Card>
          </Space>
        </div>
      </Content>

      {/* Estilos globales para la aplicación */}
      <style>{`
        .ant-table-thead > tr > th {
          background-color: ${colors.background} !important;
          color: ${colors.text};
          font-weight: 500;
          border-bottom: 1px solid ${colors.lightBorder};
        }

        .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${colors.lightBorder};
        }

        .ant-table-row-custom:hover > td {
          background-color: ${colors.background} !important;
        }

        /* Estilo para filas que contienen "OTROS" */
        .otros-row > td {
          background-color: rgba(250, 173, 20, 0.05) !important;
        }

        .otros-row:hover > td {
          background-color: rgba(250, 173, 20, 0.1) !important;
        }

        .ant-select-item-option-selected {
          background-color: ${colors.background} !important;
          font-weight: 500;
        }

        .ant-pagination-item-active {
          border-color: ${colors.primary};
        }

        .ant-pagination-item-active a {
          color: ${colors.primary};
        }

        .ant-card {
          transition: all 0.3s ease;
        }

        .ant-statistic-title {
          margin-bottom: 0px !important;
        }

        @media (max-width: 768px) {
          .ant-table {
            font-size: 12px;
          }
          .ant-table-cell {
            padding: 8px 4px !important;
          }
          .ant-tag {
            margin: 0;
            padding: 0 4px !important;
            font-size: 10px !important;
          }
          .ant-typography {
            font-size: 90%;
          }
          .ant-pagination {
            font-size: 12px;
          }
          .ant-pagination-item {
            min-width: 28px !important;
            height: 28px !important;
            line-height: 26px !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SalesLocationDistances;
